
import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import LoginPage from './components/LoginPage';
import { 
  LayoutDashboard, 
  Users, 
  KanbanSquare, 
  ListFilter, 
  Receipt, 
  Settings, 
  Bell, 
  Search,
  Plus,
  TrendingUp,
  ChevronDown,
  UserPlus,
  UserCheck,
  Rocket,
  AlertTriangle,
  Wrench,
  CheckSquare,
  CheckCircle2,
  Archive
} from 'lucide-react';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import Pipeline from './components/Pipeline';
import Finances from './components/Finances';
import CashflowForecast from './components/CashflowForecast';
import ToDoListPage from './components/ToDoListPage';
import { ViewState, ClientFilterType, Client } from './types';
import { CLIENTS as INITIAL_CLIENTS } from './constants';

// Sidebar Item Component
const NavItem: React.FC<{ 
  icon: React.ReactNode; 
  label: string; 
  active: boolean; 
  onClick: () => void;
  subItem?: boolean;
  hasSubItems?: boolean;
  expanded?: boolean;
}> = ({ icon, label, active, onClick, subItem = false, hasSubItems = false, expanded = false }) => (
  <button
    onClick={onClick}
    className={`
      w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 group
      ${active ? 'bg-slate-100 text-slate-900 border-l-2 border-slate-900' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}
      ${subItem ? 'pl-9 text-xs' : ''}
    `}
  >
    <div className="flex items-center space-x-3">
      <span className={`${active ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-600'}`}>
        {icon}
      </span>
      <span className={`font-medium ${subItem ? 'text-sm' : 'text-sm'}`}>{label}</span>
    </div>
    {hasSubItems && (
        <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${expanded ? 'transform rotate-180' : ''}`} />
    )}
  </button>
);

const App: React.FC = () => {
  // ALL hooks must be declared before any conditional returns
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>('dashboard');
  const [clientFilter, setClientFilter] = useState<ClientFilterType>('all');
  const [clientsExpanded, setClientsExpanded] = useState(false);
  const [clients, setClients] = useState<Client[]>(INITIAL_CLIENTS);

  // Check authentication status on mount
  useEffect(() => {
    let isMounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const checkAuth = async () => {
      try {
        // Check if supabase.auth exists
        if (!supabase?.auth) {
          console.warn('Supabase auth not available');
          if (isMounted) {
            setIsAuthenticated(false);
            setIsAuthReady(true);
          }
          return;
        }

        // Check existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          console.error('Auth check error:', error);
          setIsAuthenticated(false);
        } else {
          // Only set authenticated if we have a valid session
          setIsAuthenticated(!!session);
        }
        
        setIsAuthReady(true);

        // Listen for auth state changes
        try {
          const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!isMounted) return;
            setIsAuthenticated(!!session);
          });
          
          subscription = authSubscription;
        } catch (subError) {
          console.error('Failed to set up auth listener:', subError);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          setIsAuthenticated(false);
          setIsAuthReady(true);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (e) {
          // Ignore unsubscribe errors
        }
      }
    };
  }, []);

  // Show loading state while checking auth
  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginPage 
        onAuthenticated={async () => {
          // Refresh session after login to ensure it's set
          try {
            const { data: { session } } = await supabase.auth.getSession();
            setIsAuthenticated(!!session);
          } catch (error) {
            console.error('Failed to refresh session:', error);
            setIsAuthenticated(false);
          }
        }} 
      />
    );
  }

  const handleClientsClick = () => {
    setCurrentView('clients');
    setClientsExpanded(!clientsExpanded);
    if (!clientsExpanded) setClientFilter('all');
  };

  const handleClientFilterClick = (filter: ClientFilterType) => {
    setCurrentView('clients');
    setClientFilter(filter);
  };

  const handleDashboardNavigation = (view: ViewState, filter?: ClientFilterType) => {
    setCurrentView(view);
    if (view === 'clients') {
        setClientsExpanded(true);
        if (filter) {
            setClientFilter(filter);
        }
    }
  };

  const handleSyncClients = (newClients: Client[]) => {
    setClients(prev => {
        // Prepend new clients so they show at the top
        return [...newClients, ...prev];
    });
    // Force view to All Clients so the user sees the new entry immediately
    setClientFilter('all');
    setCurrentView('clients');
    setClientsExpanded(true);
  };

  const handleDeleteClient = (id: string) => {
    setClients(prev => prev.filter(c => c.id !== id));
  };

  const handleUpdateClient = (updatedClient: Client) => {
    setClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
  };

  const getClientBreadcrumbLabel = (filter: ClientFilterType) => {
    switch (filter) {
      case 'active': return "Active GMC's";
      case 'attention': return "Non-active GMC's";
      case 'insider-service': return 'Insider Service';
      case 'm1': return 'Upkeep M1';
      case 'm2': return 'Upkeep M2';
      case 'm3': return 'Upkeep M3';
      case 'm4': return 'Upkeep M4+';
      case 'finished': return 'Finished';
      case 'all': return 'All Clients';
      default: return filter.charAt(0).toUpperCase() + filter.slice(1);
    }
  };

  const renderView = () => {
    switch (currentView) {
      case 'dashboard': return <Dashboard clients={clients} onNavigate={handleDashboardNavigation} />;
      case 'clients': return (
        <Clients 
            filter={clientFilter} 
            clients={clients} 
            onSync={handleSyncClients} 
            onDelete={handleDeleteClient}
            onUpdate={handleUpdateClient}
        />
      );
      case 'pipeline': return <Pipeline />;
      case 'finances': return <Finances />;
      case 'cashflow': return <CashflowForecast />;
      case 'todo': return <ToDoListPage />;
      case 'settings': return <div className="flex items-center justify-center h-full text-slate-400 font-light">Settings View Placeholder</div>;
      default: return <Dashboard clients={clients} onNavigate={handleDashboardNavigation} />;
    }
  };

  // Authentication removed - dashboard loads immediately

  return (
    <div className="flex h-screen w-full overflow-hidden font-sans text-slate-800 selection:bg-emerald-100 selection:text-emerald-900">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-r border-white/30 bg-white/40 backdrop-blur-xl flex flex-col pt-6 pb-4 px-4 overflow-y-auto custom-scrollbar">
        {/* Brand */}
        <div className="flex items-center space-x-2 px-3 mb-8">
          <div className="w-8 h-8 rounded-lg bg-slate-900 shadow-lg flex items-center justify-center">
            <div className="w-3 h-3 border-2 border-white rounded-sm transform rotate-45"></div>
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm tracking-tight text-slate-900 leading-tight">ETHEREAL</span>
            <span className="font-light text-[10px] tracking-widest text-slate-500 uppercase">Solutions</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="space-y-1 flex-1">
          <NavItem icon={<LayoutDashboard size={18} strokeWidth={1.5} />} label="Dashboard" active={currentView === 'dashboard'} onClick={() => setCurrentView('dashboard')} />
          
          {/* Clients Section */}
          <div className="space-y-1">
            <NavItem 
                icon={<Users size={18} strokeWidth={1.5} />} 
                label="Clients" 
                active={currentView === 'clients' && clientFilter === 'all' && !clientsExpanded} 
                onClick={handleClientsClick}
                hasSubItems
                expanded={clientsExpanded}
            />
            
            {clientsExpanded && (
                <div className="space-y-0.5 animate-slide-up origin-top">
                    <NavItem icon={<Users size={14} />} label="All Clients" active={currentView === 'clients' && clientFilter === 'all'} onClick={() => handleClientFilterClick('all')} subItem />
                    <NavItem icon={<UserPlus size={14} />} label="Insider Service" active={currentView === 'clients' && clientFilter === 'insider-service'} onClick={() => handleClientFilterClick('insider-service')} subItem />
                    <NavItem icon={<UserCheck size={14} />} label="Active GMC's" active={currentView === 'clients' && clientFilter === 'active'} onClick={() => handleClientFilterClick('active')} subItem />
                    <NavItem icon={<AlertTriangle size={14} />} label="Non-active GMC's" active={currentView === 'clients' && clientFilter === 'attention'} onClick={() => handleClientFilterClick('attention')} subItem />
                    <NavItem icon={<Rocket size={14} />} label="Onboarding" active={currentView === 'clients' && clientFilter === 'onboarding'} onClick={() => handleClientFilterClick('onboarding')} subItem />
                    <NavItem icon={<Wrench size={14} />} label="Upkeep M1" active={currentView === 'clients' && clientFilter === 'm1'} onClick={() => handleClientFilterClick('m1')} subItem />
                    <NavItem icon={<Wrench size={14} />} label="Upkeep M2" active={currentView === 'clients' && clientFilter === 'm2'} onClick={() => handleClientFilterClick('m2')} subItem />
                    <NavItem icon={<Wrench size={14} />} label="Upkeep M3" active={currentView === 'clients' && clientFilter === 'm3'} onClick={() => handleClientFilterClick('m3')} subItem />
                    <NavItem icon={<Wrench size={14} />} label="Upkeep M4+" active={currentView === 'clients' && clientFilter === 'm4'} onClick={() => handleClientFilterClick('m4')} subItem />
                    <NavItem icon={<CheckCircle2 size={14} />} label="Finished" active={currentView === 'clients' && clientFilter === 'finished'} onClick={() => handleClientFilterClick('finished')} subItem />
                </div>
            )}
          </div>

          <NavItem icon={<KanbanSquare size={18} strokeWidth={1.5} />} label="Pipeline" active={currentView === 'pipeline'} onClick={() => setCurrentView('pipeline')} />
          <NavItem icon={<CheckSquare size={18} strokeWidth={1.5} />} label="To Do List" active={currentView === 'todo'} onClick={() => setCurrentView('todo')} />
          
          <div className="pt-4 pb-2">
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">Finances</div>
            <NavItem icon={<Receipt size={18} strokeWidth={1.5} />} label="Invoices" active={currentView === 'finances'} onClick={() => setCurrentView('finances')} />
            <NavItem icon={<TrendingUp size={18} strokeWidth={1.5} />} label="Cashflow Forecast" active={currentView === 'cashflow'} onClick={() => setCurrentView('cashflow')} />
          </div>
        </nav>

        {/* Bottom Actions */}
        <div className="pt-4 border-t border-slate-200/50 space-y-1">
           <NavItem icon={<Settings size={18} strokeWidth={1.5} />} label="Settings" active={currentView === 'settings'} onClick={() => setCurrentView('settings')} />
           <div className="mt-4 p-3 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl text-white shadow-lg">
              <p className="text-xs font-medium text-slate-300 mb-1">My Workspace</p>
              <div className="flex items-center justify-between">
                 <span className="text-sm font-semibold">Pro Plan</span>
                 <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
              </div>
           </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-8 border-b border-white/30 bg-white/20 backdrop-blur-sm z-10">
          <div className="flex items-center text-sm breadcrumbs text-slate-500">
             <span className="hover:text-slate-800 cursor-pointer transition-colors">Workspace</span>
             <span className="mx-2 text-slate-300">/</span>
             <span className="font-medium text-slate-800 capitalize">
                {currentView === 'clients' ? (
                   <>
                     Clients <span className="mx-2 text-slate-300">/</span> 
                     <span className="text-slate-900">
                        {getClientBreadcrumbLabel(clientFilter)}
                     </span>
                   </>
                ) : currentView === 'todo' ? 'To Do List' : currentView.replace(/([A-Z])/g, ' $1').trim()}
             </span>
          </div>

          <div className="flex items-center space-x-4">
             {/* Search */}
             <div className="relative group hidden md:block">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-slate-600 transition-colors" />
                <input 
                  type="text" 
                  placeholder="Type to command..." 
                  className="pl-9 pr-4 py-1.5 rounded-lg bg-white/50 border border-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:bg-white transition-all w-64 placeholder:text-slate-400 text-slate-700"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 border border-slate-200 px-1 rounded bg-white/50">⌘K</span>
             </div>

             <div className="h-6 w-px bg-slate-200/60"></div>

             <button className="relative p-2 text-slate-500 hover:text-slate-800 transition-colors">
                <Bell size={18} strokeWidth={1.5} />
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-400 rounded-full border-2 border-slate-50"></span>
             </button>
             
             <button 
               onClick={async () => {
                 await supabase.auth.signOut();
                 setIsAuthenticated(false);
               }}
               className="w-8 h-8 rounded-full bg-slate-900 border border-slate-700 text-white flex items-center justify-center text-xs font-bold hover:bg-slate-800 transition-colors cursor-pointer"
               title="Sign out"
             >
               JD
             </button>
          </div>
        </header>

        {/* View Content */}
        <div className="flex-1 overflow-auto p-8 relative">
           <div className="max-w-7xl mx-auto h-full flex flex-col">
              {renderView()}
           </div>
        </div>
        
      </main>
    </div>
  );
};

export default App;
